import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Upload } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AUDIENCES, SITE, CONTACT_PATHWAYS } from "@/data/content";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function GetInvolved() {
  return (
    <>
      <PageHeader
        overline="Get Involved"
        title="Four pathways. One way to connect."
        lead={`Tailored entry points for policymakers, enterprises, researchers, and technology leaders — each addresses a different need. Choose your pathway, or send a general enquiry below.`}
      />

      {/* Audience pathways */}
      <section className="ccdr-section">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {AUDIENCES.map((a, idx) => (
              <div key={a.slug} className="bg-white p-10">
                <p
                  className="text-xs uppercase tracking-[0.22em] mb-4"
                  style={{ color: "var(--ccdr-accent-dark)" }}
                >
                  Pathway 0{idx + 1}
                </p>
                <h3 className="ccdr-h3 mb-3">{a.label}</h3>
                <p className="text-[#4B5563] mb-6 leading-relaxed">{a.body}</p>
                <a
                  href={`#contact-form?pathway=${a.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    // also pre-select pathway
                    setTimeout(() => {
                      const evt = new CustomEvent("ccdr-set-pathway", { detail: a.slug });
                      window.dispatchEvent(evt);
                    }, 200);
                  }}
                  className="inline-flex items-center gap-2 text-sm border-b border-[#0B4F6C] pb-0.5 text-[#0B4F6C] hover:text-[#053043]"
                >
                  {a.cta}
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section id="contact-form" className="ccdr-section bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="ccdr-eyebrow mb-5">Send a message</p>
            <h2 className="ccdr-h2 mb-6">Contact the Centre.</h2>
            <p className="ccdr-prose">
              A single accessible form, with file upload where useful. The Centre respects your privacy and uses what
              you share only to respond to your enquiry.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              <p className="text-[#4B5563]">
                <span className="text-xs uppercase tracking-[0.22em] block mb-1 text-[#9CA3AF]">Direct</span>
                <a href={`mailto:${SITE.contactEmail}`} className="ccdr-link">{SITE.contactEmail}</a>
              </p>
              <p className="text-[#4B5563]">
                <span className="text-xs uppercase tracking-[0.22em] block mb-1 text-[#9CA3AF]">File limits</span>
                ≤ 5 MB · PDF, DOC, DOCX
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}

function ContactForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organisation: "",
    pathway: "general",
    subject: "",
    message: "",
    consent: false,
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Listen for pathway pre-select
  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, pathway: e.detail }));
    window.addEventListener("ccdr-set-pathway", handler);
    return () => window.removeEventListener("ccdr-set-pathway", handler);
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.consent) {
      setError("Consent is required to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (file) data.append("file", file);
      const r = await axios.post(`${API}/contact`, data);
      setSuccess(r.data);
      toast.success("Message received.");
      setForm({
        full_name: "",
        email: "",
        organisation: "",
        pathway: "general",
        subject: "",
        message: "",
        consent: false,
      });
      setFile(null);
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not send your message. Please try again.";
      setError(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid={CONTACT.success} className="bg-white border border-[#E5E7EB] p-10">
        <CheckCircle2 className="w-12 h-12 mb-5" style={{ color: "var(--ccdr-accent-dark)" }} />
        <h3 className="ccdr-h3 mb-3">Message received.</h3>
        <p className="text-[#4B5563] mb-6">{success.message}</p>
        <p className="text-xs uppercase tracking-[0.22em] text-[#9CA3AF]">
          Reference · {success.id.slice(0, 8)}
        </p>
        <button
          onClick={() => setSuccess(null)}
          className="ccdr-btn-outline mt-6"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid={CONTACT.form}
      onSubmit={submit}
      className="bg-white border border-[#E5E7EB] p-8 lg:p-10 space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="full_name" className="text-sm">Full name *</Label>
          <Input
            id="full_name"
            required
            value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            data-testid={CONTACT.fieldName}
            className="mt-2 rounded-none border-[#E5E7EB] focus-visible:ring-[#0B4F6C] focus-visible:border-[#0B4F6C]"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-sm">Email *</Label>
          <Input
            id="email"
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            data-testid={CONTACT.fieldEmail}
            className="mt-2 rounded-none border-[#E5E7EB] focus-visible:ring-[#0B4F6C] focus-visible:border-[#0B4F6C]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="organisation" className="text-sm">Organisation</Label>
          <Input
            id="organisation"
            value={form.organisation}
            onChange={(e) => update("organisation", e.target.value)}
            data-testid={CONTACT.fieldOrg}
            className="mt-2 rounded-none border-[#E5E7EB] focus-visible:ring-[#0B4F6C] focus-visible:border-[#0B4F6C]"
          />
        </div>
        <div>
          <Label htmlFor="pathway" className="text-sm">Pathway</Label>
          <Select value={form.pathway} onValueChange={(v) => update("pathway", v)}>
            <SelectTrigger
              id="pathway"
              data-testid={CONTACT.fieldPathway}
              className="mt-2 rounded-none border-[#E5E7EB] focus:ring-[#0B4F6C]"
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {CONTACT_PATHWAYS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="subject" className="text-sm">Subject *</Label>
        <Input
          id="subject"
          required
          value={form.subject}
          onChange={(e) => update("subject", e.target.value)}
          data-testid={CONTACT.fieldSubject}
          className="mt-2 rounded-none border-[#E5E7EB] focus-visible:ring-[#0B4F6C] focus-visible:border-[#0B4F6C]"
        />
      </div>

      <div>
        <Label htmlFor="message" className="text-sm">Message *</Label>
        <Textarea
          id="message"
          required
          rows={6}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          data-testid={CONTACT.fieldMessage}
          className="mt-2 rounded-none border-[#E5E7EB] focus-visible:ring-[#0B4F6C] focus-visible:border-[#0B4F6C]"
        />
      </div>

      <div>
        <Label htmlFor="file" className="text-sm flex items-center gap-2">
          <Upload className="w-4 h-4" /> Attachment (≤ 5 MB · PDF, DOC, DOCX)
        </Label>
        <input
          id="file"
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid={CONTACT.fieldFile}
          className="mt-2 block w-full text-sm text-[#4B5563] file:mr-4 file:py-2 file:px-4 file:border file:border-[#0B4F6C] file:bg-white file:text-[#0B4F6C] file:font-medium file:rounded-none"
        />
      </div>

      <div className="flex items-start gap-3 pt-2">
        <Checkbox
          id="consent"
          checked={form.consent}
          onCheckedChange={(v) => update("consent", !!v)}
          data-testid={CONTACT.fieldConsent}
          className="mt-1 rounded-none data-[state=checked]:bg-[#0B4F6C] data-[state=checked]:border-[#0B4F6C]"
        />
        <Label htmlFor="consent" className="text-sm leading-snug cursor-pointer">
          I consent to the Centre handling the information in this form to respond to my enquiry, in line with the
          privacy statement.
        </Label>
      </div>

      {error && (
        <div data-testid={CONTACT.error} className="bg-red-50 border border-red-200 text-red-800 p-4 text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid={CONTACT.submit}
        className="ccdr-btn-primary disabled:opacity-50"
      >
        {submitting ? "Sending…" : "Send message"} <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
}
